/**
 * Play Archive → site-wide table export source.
 *
 * The file is the archive as the coach sees it: every registry column (the
 * ones ticked in the column picker come pre-selected, in the picker's order),
 * custom stats included, one row per play in the archive's current sort +
 * filter, plus a conflict indicator column. Values are the cell text the
 * table shows, except numbers (play #, gain) which stay numeric so a coach
 * can sum them in Excel.
 */
import type { PlayView, TeamEvent } from '../../lib/api-types';
import type { ExportCell, ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fileDay } from '../integrations/export-format';

import { num, type ArchiveColumn } from './archive-columns';
import type { ColumnPrefs } from './archive-layout';
import { NOT_RECORDED } from './custom-columns';

/** Export-only column: is the play's record disputed? */
export const CONFLICT_FIELD = 'conflict_status';

function conflictText(play: PlayView): string | null {
  if (play.conflict_status === 'unresolved') {
    return `Unresolved conflict (${Math.max(play.version_count, 2)} versions)`;
  }
  if (play.conflict_status === 'resolved') return 'Resolved conflict';
  return null;
}

export function archiveCell(column: ArchiveColumn, play: PlayView): ExportCell {
  if (column.id === 'play_number') return play.play_number;
  if (column.cell === 'gain') return num(play.stats.gain_loss);
  const text = column.text(play);
  return text === NOT_RECORDED || text === '' ? null : text;
}

export function buildArchiveExportSource(input: {
  columns: readonly ArchiveColumn[];
  prefs: ColumnPrefs;
  plays: readonly PlayView[];
  event: TeamEvent | null;
  eventId: string;
}): TableExportSource {
  const byId = new Map(input.columns.map((column) => [column.id, column]));
  const visible = new Set(
    input.prefs.columns.filter((entry) => entry.visible).map((entry) => entry.id)
  );
  // The unit badge rides in the Play # cell on screen; in a file it is its own column.
  if (visible.has('play_number')) visible.add('possession');
  const ordered: ArchiveColumn[] = [
    ...input.prefs.columns
      .map((entry) => byId.get(entry.id))
      .filter((column): column is ArchiveColumn => column !== undefined),
    ...input.columns.filter(
      (column) => !input.prefs.columns.some((entry) => entry.id === column.id)
    ),
  ];
  // Play # then Unit first, like the table's first cell.
  const lead = ['play_number', 'possession'];
  const sorted = [
    ...lead.map((id) => ordered.find((column) => column.id === id)),
    ...ordered.filter((column) => !lead.includes(column.id)),
  ].filter((column): column is ArchiveColumn => column !== undefined);

  const defs: ExportColumnDef[] = [
    ...sorted.map((column) => ({
      field: column.id,
      label: column.title,
      header: column.id === 'play_number' ? 'Play #' : column.title,
      isVisible: visible.has(column.id) || column.isPinned === true,
    })),
    { field: CONFLICT_FIELD, label: 'Conflict', header: 'Conflict', isVisible: true },
  ];

  return {
    page: 'play_archive',
    pageLabel: 'Play Archive',
    columns: defs,
    rows: () =>
      input.plays.map((play) => [
        ...sorted.map((column) => archiveCell(column, play)),
        conflictText(play),
      ]),
    fileStem: exportFileStem(
      'PlayArchive',
      input.event?.name ?? null,
      fileDay(input.event?.scheduled_at ?? null)
    ),
    sheetName: 'Play Archive',
    hudl: { eventId: input.eventId, playIds: input.plays.map((play) => play.play_id) },
    rowNoun: { one: 'play', many: 'plays' },
  };
}
